import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, forwardRef, Input, OnInit,
  Output, Inject, ViewChild, ViewContainerRef, ComponentFactoryResolver, ContentChild, TemplateRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileUploadService, FileProcessorOptions } from '../../../services/file-upload-service';
import { DynamicModalLoaderService } from '../../../../layout/services/dynamic-modal-loader.service';
import { BasicModalComponent } from '../../../../layout/components/modals/basic-modal/basic-modal.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { ConfirmModalComponent } from '../../../../layout/components/modals/confirm-modal/confirm-modal.component';
import { modalResponse } from '../../../../layout/constants/modal-responses.enum';
import { UploadedFileDetailsComponent } from '@ispm/dynamic-form/components/uploaded-file-details/uploaded-file-details.component';
import { IdynamicFormItem } from '../../interfaces/IdynamicFormItem';
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { StringUtils } from "@ispm/shared/utils/String.utils";

type FieldTypes = 'date' | 'string';

interface FileValidationError {
  title: string;
  error?: string[] | string;
}

function isFileValidationError(obj: any): obj is FileValidationError {
  return typeof obj.title === 'string' && (obj.error === undefined || obj.error === null ||
    Array.isArray(obj.error) || typeof obj.error === 'string');
}


@Component({
  selector: 'gen-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => FileUploadComponent),
    multi: true
  }],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileUploadComponent implements OnInit, ControlValueAccessor {

  constructor(private uploadService: FileUploadService, private cd: ChangeDetectorRef,
              private dynamicModalLoader: DynamicModalLoaderService, private translateService: TranslateService,
              private notificationMessageService: NotificationMessageService,
              private notificationMessage: NOTIFICATION_MESSAGE) {
    this.uploadImagePath = '/assets/fileupload/upload-image.svg';
  }

  @Input()
  set data(formData) {
    if (formData && formData.name) {
      this.fileName = formData.name;
    }
  }
  @Input() displayType: 'dropzone' | 'input' = 'dropzone';
  @Input() item: IdynamicFormItem;
  @Input() maxFileSize: number;
  @Input() storageName: string;
  @Input() fileFields: Array<{ label: string, value: string | number, type?: FieldTypes }>;
  @Input() accept: string;
  @Input() preUploadWarning: { key?: string, text?: string };
  @Input() fileButtonText: string;
  @Input() checksumAlgorithm: string;
  @Input() fileProcessorOptions: FileProcessorOptions;
  @Input() useFriendlyName: boolean;
  @Input() fileExtension: string;
  @Input() waitForUpload: boolean;
  @Input() processorType: string;
  @Input() label: string;
  @Input() disabled: boolean;
  @Input() skipFileExtValidation: boolean;
  @Input() showDragAndDrop: boolean = true;
  @Input() uploadedFileDetailsComponentName: string = 'UploadedFileDetailsComponent';
  @Output() onFocus: EventEmitter<Event> = new EventEmitter();
  @Output() uploadEmitter: EventEmitter<any> = new EventEmitter();
  @Output() deleteEmitter: EventEmitter<any> = new EventEmitter();

  // THE NAME OF THIS EVENT IS IMPORTANT (change) this is our way to communicate with angular forms event hook
  @Output() change: EventEmitter<any> = new EventEmitter();

  @ContentChild(TemplateRef, {static: false}) template: TemplateRef<any>;

  public uploadImagePath: string;
  public uploadInProgress: boolean = false;
  public displayWarning: boolean = false; // TODO: Get this from configuration, also paths.
  public warningImageUrl: string;
  public filePath: string;
  public preUploadWarningText: string = '';
  public preUploadWarningCss: string = '';
  public maxFileNameLength: number = -1;
  readonly warningText = 'Deviation in PCR protocol is detected, please consider that this might lead to inaccurate results. Please consult the executing technician or the OncoSignal team.';
  private readonly fileUploadTranslationPrefix = 'fileUpload.';
  private readonly validationErrorTranslationPrefix = `${this.fileUploadTranslationPrefix}validation.`;
  private fileName: string = '';
  private onUpload: (_?: any) => void = (args) => this.uploadEmitter.emit(args);
  private onDelete: (_?: any) => void = (args) => this.deleteEmitter.emit(args);

  ngOnInit() {
    if (this.preUploadWarning) {
      if (this.preUploadWarning.key) {
        const translatedObj: { message?: string, title?: string } = this.translateService.instant(`${this.fileUploadTranslationPrefix}${this.preUploadWarning.key}`);
        this.preUploadWarningText = translatedObj.message ? translatedObj.message : '';
      } else if (this.preUploadWarning.text) {
        this.preUploadWarningText = this.preUploadWarning.text;
      }
    }

    if (this.item && this.item.attributes && this.item.attributes['[maxFileNameLength]'] !== undefined) {
      this.maxFileNameLength = this.item.attributes['[maxFileNameLength]'];
    }

    this.waitForUpload = this.fileName === '';
    if (this.item && this.item['preUploadWarningClass'] !== undefined) {
      this.preUploadWarningCss = this.item['preUploadWarningClass'];
    }

    this.waitForUpload = this.fileName === "";
    if (!this.waitForUpload) {
      const fileObj = { };
      fileObj['name'] = this.fileName;
      this.setAfterUploadFields(fileObj);
    }
  }

  writeValue(value: any): void {
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onUpload = (args) => {
      fn(args);
      this.uploadEmitter.emit(args);
    };
    this.onDelete = (args) => {
      fn(args);
      this.deleteEmitter.emit(args);
    };
  }

  registerOnTouched(fn: any): void {
  }

  onDrag(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  onUploadInput(event) {
    if (event.target && event.target.files) {
      if (!this.skipFileExtValidation) {
        if (!this.validateExtension(event)) {
          return;
        }
      }

      if (StringUtils.isInvalidCharsInFileName(event.target.files[0].name)) {
        this.notificationMessageService.error(`${this.notificationMessage.FILENAME_CONTAINS_HTML}`);
        return;
      }

      const eventData: File = event.target.files[0];
      this.uploadFile(eventData);
      this.onFocus.emit(event);
    }
  }

  validateExtension(event): boolean {
    let isValid: boolean = false;
    if (event.target.value && event.target.accept) {
      const fileName = event.target.value;
      const lastIndex = fileName.lastIndexOf('.');
      if (lastIndex > 0) {
        const extension = fileName.substr(lastIndex);
        const allowedExtensions = event.target.accept.split(',');
        if (allowedExtensions.length > 0) {
          for (const ext of allowedExtensions) {
            if (extension.toUpperCase() === ext.toUpperCase()) {
              isValid = true;
              break;
            }
          }
        }
      }
    }
    if (!isValid) {
      this.notificationMessageService.error(`${this.notificationMessage.CANNOT_UPLOAD_FILE_EXPECTED_EXTENSION} ${event.target.accept}`);
    }
    return isValid;
  }

  onDrop(event: DragEvent) {
    const files = event.dataTransfer.files;
    const eventData: File = files[0];
    this.uploadFile(eventData);
    this.onFocus.emit(event);
    event.preventDefault();
  }

  setAfterUploadFields(source: any) {
    for (const fileIndex of this.fileFields) {
      fileIndex.value = source[fileIndex.label];
    }
  }

  deleteFile() {
    this.dynamicModalLoader.popupModal(ConfirmModalComponent, {
      title: this.translateService.instant(`${this.fileUploadTranslationPrefix}${this.processorType}_delete_confirm.title`),
      content: this.translateService.instant(`${this.fileUploadTranslationPrefix}${this.processorType}_delete_confirm.message`),
      approveBtn: 'Delete',
      declineBtn: 'Cancel'
    }).map(response => response.payload)
      .filter(response => response > 0)
      .take(1)
      .filter((response: modalResponse) => response === modalResponse.approve)
      .subscribe(() => {
        this.waitForUpload = true;
        this.filePath = undefined;
        this.change.emit();
        this.onDelete('deleted');
        this.cd.markForCheck();
        this.cd.detectChanges();
      });
  }

  buildFileProcessorOptions() {
    const options: FileProcessorOptions = this.fileProcessorOptions ? this.fileProcessorOptions : { };

    if (this.fileExtension) {
      options.acceptedFileExtensions = this.fileExtension.split(',');
    }

    return options;
  }

  private uploadFile(file: File) {
    this.uploadInProgress = true;
    this.waitForUpload = false;
    this.filePath = file.name;

    if (this.maxFileNameLength > -1 && this.filePath.length > this.maxFileNameLength) {
      this.onValidationError({ title: 'max_file_length_error' });
      return;
    }

    const noFriendlyName = !this.useFriendlyName ? 'true' : 'false';

    const uploadOptions = {
      processorType: this.processorType,
      noFriendlyName: noFriendlyName,
      checksumAlgorithm: this.checksumAlgorithm,
      fileProcessorOptions: this.buildFileProcessorOptions()
    };
    // this.uploadService.uploadFormData<any | FileValidationError>(file, this.storageName, this.processorType, false, noFriendlyName, this.checksumAlgorithm)
    this.uploadService.uploadFormData<any | FileValidationError>(file, this.storageName, uploadOptions, false)
      .catch(() =>
        Observable.of({ body: { title: `${this.processorType}_server_processing_failure` } })
      )
      .subscribe(response => {
        this.uploadInProgress = false;
        if (isFileValidationError(response.body)) {
          this.onValidationError(response.body);
          return;
        }

        this.setAfterUploadFields(response.body);
        this.warningImageUrl = response.body.warning;
        this.onUpload({ ref: response.body.referenceId, ...response.body});
        this.cd.markForCheck();
        this.cd.detectChanges();
      });
  }

  private onValidationError(response: FileValidationError) {
    this.waitForUpload = true;
    this.filePath = undefined;
    this.dynamicModalLoader.popupModal(BasicModalComponent, {
      title: this.translateService.instant(`${this.validationErrorTranslationPrefix}${response.title}.title`),
      content: this.translateService.instant(`${this.validationErrorTranslationPrefix}${response.title}.message`, { error: response.error}),
      approveBtn: 'Close',
      backdropAllowed: false
    });
    this.cd.markForCheck();
    console.log('Got validation error:', response);
  }

  public getDeleteFileCallback() {
    return ($event) => this.deleteFile();
  }
}
